"use client";

import { AuctionItem } from "@/types/auction";

import Button from "@/components/Elements/Button";
import { getAuctionItemLink } from "@/lib/links";
import Link from "next/link";
import classNames from "classnames";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import NoImageFound from "@/components/NoImageFound";
import {
  BID_TIME_REMAINING_COUNTDOWN,
} from "@/lib/auctions";
import useConfirmedEnded from "@/hooks/useConfirmedEnded";
import useUserStore from "@/stores/useUserStore";
import Image from "next/image";
import { useShallow } from "zustand/react/shallow";
import useAuctionItemFavorite from "@/hooks/useAuctionItemFavorite";
import { useSearchParams } from "next/navigation";
import ArrowButton from "@/components/Elements/ArrowButton";
import { formatPrice, parseDate, parseToDate } from "@/lib/utils";
import PopUpSvg from "@/icons/popup.svg";
import "swiper/css";
import "swiper/css/pagination";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import useModalStore from "@/hooks/useModalStore";
import { MarkerSvg } from "@/lib/temp";
import ModalElement from "@/components/Modal/Modal";
import AuctionBidField from "../../AuctionBidField";
import AuctionBidHistory from "../../AuctionBidHistory";
import AuctionEndedImage from "../../AuctionEndedImage";
import FavoriteButton from "../FavoriteButton";
import CurrentBid from "../CurrentBid";
import BidCountDown from "../BidCountDown";
import ListLeadingBid from "../ListLeadingBid";

interface AuctionCallCardProps {
  item: AuctionItem;
  prefetchedBidsData?: any;
  fetchBids?: boolean;
  isFetching?: boolean;
  user?: any;
  totalItems?: number | null;
  serverTime: any;
  isLoading?: boolean;
}

const FavoriteButtonMemomized = React.memo(
  ({ auctionItemFavorite }: { auctionItemFavorite: any }) => (
    <FavoriteButton
      auctionItemFavorite={auctionItemFavorite}
      extraClassName="!relative !m-0 !p-0"
    />
  )
);

const AuctionBidFieldMemomized = React.memo(
  ({
    hasEnded,
    leadingAutoBid,
    auctionItem,
    leadingBid,
    isLeadingAutoBid,
    ref,
  }: {
    hasEnded: boolean;
    auctionItem: AuctionItem;
    leadingBid: number;
    leadingAutoBid: any;
    isLeadingAutoBid: boolean;
    ref: any;
  }) => (
    <AuctionBidField
      hasEnded={hasEnded}
      leadingAutoBid={leadingAutoBid}
      auctionItem={auctionItem}
      leadingBid={leadingBid}
      ref={ref}
      isLeadingAutoBid={isLeadingAutoBid}
    />
  )
);

AuctionBidFieldMemomized.displayName = "AuctionBidFieldMemomized";
FavoriteButtonMemomized.displayName = "FavoriteButtonMemomized";

const ListViewItem: React.FC<AuctionCallCardProps> = ({
  item,
  prefetchedBidsData,
  serverTime,
  isLoading,
  isFetching = false,
}) => {
  const itemId = Number(item.itemId);
  const searchParams = useSearchParams();
  const linkRef = searchParams?.get("sida") || "1";
  const cardRef = useRef<HTMLDivElement>(null);
  const bidDivRef = useRef<HTMLDivElement>(null);
  const inputBidFieldRef = useRef<HTMLDivElement>(null);
  const { loginModal } = useModalStore();
  const [bidModal, setBidModal] = useState({
    isOpen: false,
    content: "",
  });
  const { user, userInfo } = useUserStore(useShallow((state) => state));
  const [showAllBids, setSHowAllBids] = useState(false);
  const itemLink = item && getAuctionItemLink(item, linkRef);
  const hasEnded = useConfirmedEnded(item?.itemEndTime, serverTime);

  const cardId = `auction-item-card-${itemId}`;

  const itemNameFormatted = `${
    item.itemSortNo ? `Rop ${item.itemSortNo}: ` : " "
  } ${item.itemTitle}`;

  const auctionItemFavorite = useAuctionItemFavorite({
    itemId: itemId,
    userId: user?.id,
  });

  const bidData = prefetchedBidsData;

  const leadingAutoBid = bidData?.leadingAutoBid;

  const isLeadingAutoBid =
    leadingAutoBid && Number(leadingAutoBid?.autoUserId) === Number(user?.id);

  const isWinner =
    user && bidData ? item?.itemWinningUserId === user?.id : false;

  const leadingBid = bidData?.bids
    ? bidData?.bids[0]?.historyBidValue
    : item.itemMinBid;

  const containerClass = classNames(
    "bg-light-grey rounded-xl overflow-hidden flex flex-col items-start justify-start box-border transition-shadow duration-300 ease-in-out"
  );

  const bidModalContent = () => (
    <div className="mt-0">
      <div className="w-full flex flex-col self-stretch relative font-semibold text-base mb-4 p-5">
        <span className="font-normal">
          {hasEnded
            ? "Slutpris"
            : bidData && bidData?.bids?.length > 0
            ? "Ledande bud"
            : "Utgångspris"}
          :{" "}
        </span>
        <span className="text-2xl">
          <CurrentBid
            auctionItem={item}
            bids={bidData?.bids}
            showLeadingUser
            small
          />
        </span>
      </div>
      <div
        className={classNames(
          "p-6 flex flex-col items-start justify-start gap-[24px] bg-aliceblue text-paragraph"
        )}
        ref={bidDivRef}
      >
        <BidCountDown
          item={item}
          hasEnded={hasEnded}
          isWinner={isWinner}
          bidData={bidData}
          serverTime={serverTime}
          isFetching={isFetching}
        />
        <AuctionBidFieldMemomized
          leadingAutoBid={leadingAutoBid}
          hasEnded={hasEnded}
          auctionItem={item}
          leadingBid={leadingBid}
          isLeadingAutoBid={isLeadingAutoBid}
          ref={inputBidFieldRef}
        />
        <div className="bg-aliceblue py-4 w-full">
          <AuctionBidHistory
            auctionItem={item}
            user={user}
            hasEnded={hasEnded}
            bidData={bidData}
            bidIsLoading={false}
            showAllBids={showAllBids}
            setSHowAllBids={setSHowAllBids}
            noMaxHeight
            extraClass="max-h-[300px]"
          />
        </div>
      </div>
    </div>
  );

  const openBidModal = () => {
    if (!bidModal || !loginModal) return;

    if (!user?.id) {
      loginModal.setTitle("Logga in på Tovek.se");
      loginModal.onOpen();
      return;
    }

    setBidModal((prev: any) => ({ ...prev, isOpen: true }));
  };

  if (!item?.itemMinBid) {
    return (
      <div id={cardId} className={containerClass} ref={cardRef}>
        <div className="w-full overflow-x-auto md:overflow-visible">
          <table className="min-w-full bg-white text-left table-auto border-separate border-spacing-y-2 min-h-[80px]">
            <tbody>
              <tr className="block md:table-row">
                {/* Column: Title & Favorite */}
                <td className="py-1 px-3 w-full md:w-1/3 block md:table-cell">
                  <div className="flex flex-row space-x-2 items-start">
                    <FavoriteButton
                      auctionItemFavorite={auctionItemFavorite}
                      extraClassName="!relative !m-0 !p-0 invisible pointer-events-none"
                    />
                    <div className="text-md font-semibold text-blue">
                      <Link href={itemLink || "#"} className="!no-underline" prefetch={false}>
                        <span className="font-bold">
                          {item.itemSortNo ? `Rop ${item.itemSortNo}: ` : ""}
                        </span>
                        <h3 className="text-base inline font-normal text-blue">
                          Utgår
                        </h3>
                      </Link>
                    </div>
                  </div>
                </td>

                {/* Column: Location */}
                <td className="py-1 px-3 w-full md:w-1/6 block md:table-cell pb-4 md:pb-1"></td>

                {/* Column: Countdown */}
                <td className="py-1 px-3 w-full md:w-1/5 block md:table-cell"></td>

                {/* Column: Bid */}
                <td className="py-1 px-3 w-full md:w-1/5 block md:table-cell text-left md:text-right"></td>

                {/* Column: Button */}
                <td className="py-1 px-3 w-full md:w-2/5 block md:table-cell text-left md:text-right"></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div id={cardId} className={containerClass} ref={cardRef}>
      <ModalElement
        isOpen={bidModal?.isOpen}
        modalTitleClass="!mb-0 p-5"
        wrapperExtraClass="!p-0"
        size="lg"
        modalTitle={itemNameFormatted}
        modalBody={bidModalContent()}
        onClose={() => {
          setBidModal((prev: any) => ({ ...prev, isOpen: false }));

          if (cardRef.current) {
            cardRef.current.scrollIntoView({ behavior: "smooth" });
          }
        }}
      />

      <div className="w-full overflow-x-auto md:overflow-visible">
        <table className="min-w-full bg-white text-left table-auto border-separate border-spacing-y-2 min-h-[80px]">
          <tbody>
            <tr className="block md:table-row">
              {/* Column: Title & Favorite */}
              <td className="py-1 px-3 w-full md:w-1/3 block md:table-cell">
                <div className="flex flex-row space-x-2 items-start">
                  <FavoriteButtonMemomized
                    auctionItemFavorite={auctionItemFavorite}
                  />
                  <div className="text-md font-semibold text-blue">
                    <Link href={itemLink || "#"} className="!no-underline" prefetch={false}>
                      <span className="font-bold">
                        {item.itemSortNo ? `Rop ${item.itemSortNo}: ` : ""}
                      </span>
                      <h3 className="text-base inline font-normal text-blue">
                        {item.itemTitle}
                      </h3>
                    </Link>
                  </div>
                </div>
              </td>

              {/* Column: Location */}
              <td className="py-1 px-3 w-full md:w-1/6 block md:table-cell pb-4 md:pb-1">
                {item?.address?.addressTitle && (
                  <Link
                    href={`https://www.google.com/maps/search/?api=1&query=${item.address.addressTitle}`}
                    target="_blank"
                    className="!no-underline hover:text-blue "
                  >
                    <div className="flex flex-row space-x-2 items-center text-xs md:text-16">
                      <span className="text-muted-bg">
                        <MarkerSvg />
                      </span>
                      <span className="small-text-below text-gray-700 md:text-paragraph !font-semibold text-xs md:text-1xl">
                        {item.address.addressTitle}
                      </span>
                    </div>
                  </Link>
                )}
              </td>

              {/* Column: Countdown */}
              <td className="py-1 px-3 w-full md:w-1/5 block md:table-cell">
                <div className="md:hidden font-semibold text-muted mb-1 text-14">
                  Tid kvar
                </div>
                <div className="small-text-below text-gray-700 md:text-paragraph">
                  <BidCountDown
                    isFetching={isFetching}
                    item={item}
                    hasEnded={hasEnded}
                    isWinner={isWinner}
                    bidData={bidData}
                    serverTime={serverTime}
                  />
                </div>
              </td>

              {/* Column: Bid */}
              <td className="py-1 px-3 w-full md:w-1/5 block md:table-cell text-left md:text-right">
                <div className="md:hidden font-semibold text-muted mb-1 text-14">
                  Ledande bud
                </div>

                <div className="small-text-below text-gray-700 md:text-paragraph">
                  <ListLeadingBid
                    auctionItem={item}
                    bids={bidData?.bids}
                    showLeadingUser
                    small
                  />
                </div>
              </td>

              {/* Column: Button */}
              <td className="py-1 px-3 w-full md:w-2/5 block md:table-cell text-left md:text-right">
                {!hasEnded ? (
                  <Button
                    onClick={openBidModal}
                    className="text-xs px-4 md:px-4 mt-1 md:ml-auto !w-fit !h-full"
                  >
                    Lägg bud
                  </Button>
                ) : (
                  <Link
                    href={itemLink || "#"}
                    className="!no-underline"
                    shallow
                  >
                    <Button
                      bgColor="white"
                      textColor="blue"
                      variant="outlined"
                      borderColor="blue"
                      className="hover:bg-blue hover:text-white !hover:bg-opacity-1 text-xs px-4 px-4 md:px-2 ml-auto mt-1"
                    >
                      Öppna rop på egen flik
                    </Button>
                  </Link>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ListViewItem;
